import {poll_add_Api} from 'apis'
import {ask_create_Api} from 'apis/ask'
import {getId, getType} from 'apis/model/base'
import CreatePostForm from 'components/Feed/InputBoard/CreatePostForm'
import {PostPrivacyTypes} from 'components/Feed/InputBoard/PostPrivacy'
import {LoginContext} from 'components/LoginContext'
import {Formik, FormikConsumer} from 'formik'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {NavigationContext} from 'modules/navigation/NavigationContext'
import React, {useCallback, useContext} from 'react'
import {createRequiredLogin, ModalLayout} from 'routes/createRequiredLogin'
import AskEditorForm from 'views/Asks/AskEditorForm'
import CreatePoll from 'views/CreatePoll'
import NoFoundPage from 'views/NoFoundPage'
import ServerContext from 'views/Server/ServerContext'
import {getResponseItem, NullAsDefault, renderOwnChild} from 'views/Shared'
import {ObserverContext} from 'views/Shared/components/ObservedList'
import {notifyOnError} from './createErrorEvent'
import {NotificationActionTypes, successNotify} from './createNotification'
import {LayoutContext} from '../../../components/layouts/Default/LayoutContext'
import {FullSideModalLayout} from '../../../components/Modal/SideModalLayout'
import {PostFormContext} from '../../../components/Feed/PostForm/PostFormContext'

// ─── Post type constants ──────────────────────────────────────────────────────

export const PostTypes = Object.freeze({
    ASK: 'ask',
    POLL: 'poll',
    EMBED: 'embed',
    IMAGES: 'images',
    DEFAULT: 'default',
    ARTICLE: 'article',
    PRAYER: 'prayer',
})

// ─── PollPost ─────────────────────────────────────────────────────────────────

const PollPost = ({id, type, onDismiss, onSuccess = NullAsDefault, Wrapper = renderOwnChild}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {addPost = NullAsDefault} = useContext(ObserverContext)
    const {options} = useContext(PostFormContext)

    const container = {id, type}

    const initialValues = {
        container_id: id,
        containers: options,
        container_type: type,
        default_owner_id: getId(login),
        default_container_id: getId(container),
        privacy: PostPrivacyTypes.public.value,
    }

    const {isLoading, handleAsyncAction} = useAsyncAction({
        apiInfo: poll_add_Api,
        onSuccess: (result, {response}) => {
            const newItem = getResponseItem(response)
            successNotify(NotificationActionTypes.CREATE, t)
            addPost(newItem)
            onSuccess(newItem)
        },
        onError: notifyOnError(t),
    })

    return (
        <Wrapper>
            <Formik initialValues={initialValues} enableReinitialize={true}>
                <FormikConsumer>
                    {({values}) => (
                        <CreatePoll
                            initialValues={values}
                            isLoading={isLoading}
                            onSubmit={(values) => {
                                const {
                                    owner, container, containers,
                                    container_id, container_type,
                                    default_owner_id, default_container_id,
                                    ...rest
                                } = values ?? {}

                                handleAsyncAction(
                                    {
                                        owner_id: getId(owner?.data) ? getId(owner?.data) : getId(login),
                                        owner_type: getId(owner?.data) ? getType(owner?.data) : "user",
                                        ...rest,
                                    },
                                    {
                                        id: getId(container?.data) ? getId(container?.data) : default_container_id,
                                        prop: getId(container?.data) ? getType(container?.data) : "channel",
                                    }
                                )
                            }}
                            onCancel={onDismiss}
                        />
                    )}
                </FormikConsumer>
            </Formik>
        </Wrapper>
    )
}

// ─── AskPost ──────────────────────────────────────────────────────────────────

const AskPost = ({id, type, onDismiss, onSuccess = NullAsDefault, Wrapper = renderOwnChild}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {addPost = NullAsDefault} = useContext(ObserverContext)
    const {options} = useContext(PostFormContext)

    const container = {id, type}

    const initialValues = {
        container_id: id,
        containers: options,
        container_type: type,
        default_owner_id: getId(login),
        default_container_id: getId(container),
        privacy: PostPrivacyTypes.public.value,
    }

    const {isLoading, handleAsyncAction} = useAsyncAction({
        apiInfo: ask_create_Api,
        onSuccess: (result, {response}) => {
            const newItem = getResponseItem(response)
            successNotify(NotificationActionTypes.CREATE, t)
            addPost(newItem)
            onSuccess(newItem)
        },
        onError: notifyOnError(t),
    })

    return (
        <Wrapper>
            <AskEditorForm
                isLoading={isLoading}
                initialValues={initialValues}
                onSubmit={(values) => {
                    const {
                        owner, container, containers,
                        container_id, container_type,
                        default_owner_id, default_container_id,
                        ...rest
                    } = values ?? {}
                    handleAsyncAction(
                        {
                            owner_id: getId(owner?.data) ? getId(owner?.data) : getId(login),
                            owner_type: getId(owner?.data) ? getType(owner?.data) : "user",
                            ...rest,
                        },
                        {
                            id: getId(container?.data) ? getId(container?.data) : default_container_id,
                            prop: getId(container?.data) ? getType(container?.data) : "channel",
                        }
                    )
                }}
                onCancel={onDismiss}
            />
        </Wrapper>
    )
}

// ─── SimplePost ───────────────────────────────────────────────────────────────

const SimplePost = ({id, type, onDismiss}) => {
    const login = useContext(LoginContext)
    return (
        <CreatePostForm
            onCancel={onDismiss}
            initialValues={{
                default_container_id: id,
                default_owner_id: getId(login),
            }}
        />
    )
}

// ─── PrayerPost ───────────────────────────────────────────────────────────────

const PrayerPost = ({id, type, onDismiss}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    return (
        <CreatePostForm
            onCancel={onDismiss}
            initialValues={{
                post_type: 'prayer',
                prayer_category: t('thanksgiving'),
                default_container_id: id,
                default_owner_id: getId(login),
            }}
        />
    )
}

// ─── Route → Component map ────────────────────────────────────────────────────

const getComponentByType = (type) => {
    switch (type) {
        case PostTypes.PRAYER:
            return PrayerPost
        case PostTypes.DEFAULT:
            return SimplePost
        case PostTypes.POLL:
            return PollPost
        case PostTypes.ASK:
            return AskPost
        default:
            return null
    }
}

// ─── PostScreenComponent ──────────────────────────────────────────────────────
//
// Renders the appropriate post form inside a modal (desktop) or side panel (mobile).
//
// Channel context comes from PostFormContext.current — set by PostSettingForm.
// No fallback to ChannelCreatePost needed: PostFormProvider auto-selects a channel.

const PostScreenComponent = ({Component, ...props}) => {
    const {setCurrent: setServerCurrent = NullAsDefault} = useContext(ServerContext)
    const {current, options, setCurrent = NullAsDefault} = useContext(PostFormContext)
    const {isSm} = useContext(LayoutContext)
    const {history} = useContext(NavigationContext)

    const {container} = current ?? {}
    const id = getId(container)
    const type = getType(container)

    const onCancel = () => setServerCurrent(null)

    const sharedProps = {
        ...props,
        id,
        type,
        onDismiss: () => {
            onCancel()
            setCurrent(null)
        },
        onSuccess: () => {
            onCancel()
            setCurrent(null)
            history.push({state: {refreshToken: Date.now()}})
        },
    }

    const renderContent = useCallback(() => {
        if (isSm) {
            return (
                <FullSideModalLayout renderHeader={() => null}>
                    <div
                        className="p-3 w-full relative flex flex-col"
                        style={{
                            minHeight: '100dvh',
                            paddingBottom: 'env(safe-area-inset-bottom, 16px)',
                        }}>
                        <Component {...sharedProps} />
                    </div>
                </FullSideModalLayout>
            )
        }
        return (
            <ModalLayout
                width={600}
                closable={false}
                onCancel={onCancel}>
                <Component {...sharedProps} />
            </ModalLayout>
        )
    }, [current, isSm, id, type])

    // PostFormProvider guarantees options are loaded and current is set.
    // If somehow both are absent, render nothing gracefully.
    const hasContext = Boolean(container || options.length)
    if (!hasContext) return null

    return renderContent()
}

// ─── Factory ──────────────────────────────────────────────────────────────────

const createPost = (type) => {
    const Component = getComponentByType(type)

    if (!Component) {
        return () => (
            <div className="flex w-full justify-center">
                <NoFoundPage/>
            </div>
        )
    }

    return createRequiredLogin()((props) => (
        <PostScreenComponent Component={Component} {...props} />
    ))
}

export default createPost